import React, { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import OmkarLogo from "@/components/OmkarLogo";
import { api, formatErr, resolveImage } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    api.get("/whitelabel")
      .then((res) => setBranding(res.data))
      .catch(() => {});
  }, []);

  if (user) return <Navigate to="/dashboard" replace />;

  const companyName = branding?.company_name || "Omkar Milk Distributor";
  const tagline = branding?.tagline || "Distributor";
  const description = branding?.company_description || "The mini-ERP built for Omkar Milk Distributor. Per-customer pricing, daily orders, ledger with advance adjustment, and instant invoicing — all in one place.";
  const logoUrl = branding?.logo_url ? resolveImage(branding.logo_url) : null;

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(username, password, mfaRequired ? mfaCode : null);
      if (result && result.mfa_required) {
        setMfaRequired(true);
        toast.info("Google Authenticator MFA enabled. Enter 6-digit code to continue.");
        return;
      }
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/1276237/pexels-photo-1276237.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-cobalt-700/80 via-cobalt-600/60 to-slate-900/70" />
        <div className="relative z-10 flex flex-col justify-between p-10 text-white w-full">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt={companyName} className="h-11 max-w-[50px] object-contain shrink-0" />
            ) : (
              <OmkarLogo size={44} />
            )}
            <div className="font-heading text-xl font-bold tracking-tight leading-tight">
              {companyName}<br/><span className="text-sm font-medium tracking-[0.22em] uppercase text-white/70">{tagline}</span>
            </div>
          </div>
          <div>
            <h1 className="font-heading text-4xl lg:text-5xl font-bold tracking-tighter leading-tight">
              Dairy Distributor<br/>Management System
            </h1>
            <p className="mt-4 text-white/80 text-sm max-w-md leading-relaxed">
              {description}
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 text-xs uppercase tracking-[0.2em] text-white/70">
              <div>Pricing Matrix</div>
              <div>Ledger</div>
              <div>Invoicing</div>
            </div>
          </div>
          <div className="text-xs text-white/60">© {new Date().getFullYear()} {companyName}</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-[#F8FAFC]">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="md:hidden flex items-center gap-3 mb-6">
            {logoUrl ? (
              <img src={logoUrl} alt={companyName} className="h-10 max-w-[44px] object-contain shrink-0" />
            ) : (
              <OmkarLogo size={40} />
            )}
            <div>
              <div className="font-heading text-lg font-bold leading-none">{companyName}</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mt-1">{tagline}</div>
            </div>
          </div>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            {mfaRequired ? "MFA Verification" : "Sign in"}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {mfaRequired ? "Open Google Authenticator and enter your 6-digit passcode." : "Welcome back. Enter your credentials to continue."}
          </p>

          <div className="mt-8 space-y-4">
            {!mfaRequired ? (
              <>
                <div>
                  <Label htmlFor="username" className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Username or Email</Label>
                  <Input id="username" data-testid="login-username" value={username} onChange={(e)=>setUsername(e.target.value)} className="mt-1.5" required />
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Password</Label>
                  <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e)=>setPassword(e.target.value)} className="mt-1.5" required />
                </div>
              </>
            ) : (
              <div>
                <Label htmlFor="mfaCode" className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Google Authenticator Code</Label>
                <Input
                  id="mfaCode"
                  data-testid="input-mfa-code"
                  type="text"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e)=>setMfaCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="mt-1.5 font-mono text-lg text-center tracking-widest"
                  autoFocus
                  required
                />
              </div>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full mt-6 bg-cobalt-500 hover:bg-cobalt-700 text-white font-semibold"
          >
            <Lock size={16} className="mr-2"/> {loading ? "Verifying…" : mfaRequired ? "Verify Code & Sign in" : "Sign in"}
          </Button>

          {mfaRequired && (
            <button
              type="button"
              onClick={() => { setMfaRequired(false); setMfaCode(""); }}
              className="mt-3 text-xs text-slate-500 hover:text-slate-800 text-center w-full block"
            >
              ← Back to Login Credentials
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
